import { ApiClient } from '@/common/api/ApiClient';
import { z } from 'zod';
import {
  accessSchema,
  createSupplyPointRequestSchema,
  supplyPointSchema,
  supplyPointTypeSchema,
  updateSupplyPointRequestSchema,
  type Access,
  type CreateAccessRequest,
  type CreateSupplyPointRequest,
  type SupplyPoint,
  type UpdateAccessRequest,
  type UpdateSupplyPointRequest,
} from './schemas';

const mockSupplyPoints: SupplyPoint[] = [
  {
    id: 1,
    name: 'Prague Office',
    systemType: 1,
    partners: [],
    latitude: '50.0755381',
    longitude: '14.4378005',
    altitude: '202.00',
    street: 'Václavské náměstí 1',
    city: 'Prague',
    zipCode: '110 00',
    country: 'CZ',
    authorizedRole: 'full',
  },
  {
    id: 2,
    name: 'Brno Warehouse',
    systemType: 2,
    partners: [{ id: 1, name: 'Tech Solutions s.r.o.' }],
    latitude: '49.1950602',
    longitude: '16.6068371',
    altitude: '227.00',
    street: 'Nám. Svobody 5',
    city: 'Brno',
    zipCode: '602 00',
    country: 'CZ',
    authorizedRole: 'write',
  },
  {
    id: 3,
    name: 'Berlin Site',
    systemType: 1,
    partners: [],
    latitude: '52.520008',
    longitude: '13.404954',
    altitude: '34.50',
    street: 'Unter den Linden 10',
    city: 'Berlin',
    zipCode: '10117',
    country: 'DE',
    authorizedRole: 'read',
  },
];

const mockTypes = [
  { id: 1, name: 'ProsumoBox' },
  { id: 2, name: 'EnergoStation' },
  { id: 3, name: 'Loxone' },
  { id: 4, name: 'Shelly' },
];

// Accesses per supply point id
const mockAccessesBySupplyPoint: Record<number, Access[]> = {
  1: [
    {
      id: 1,
      system: 1,
      user: { email: 'alice@example.com', firstName: 'Alice', lastName: 'Smith' },
      role: 'full',
    },
    {
      id: 2,
      system: 1,
      user: { email: 'bob@example.com', firstName: 'Bob', lastName: 'Novák' },
      role: 'read',
    },
    {
      id: 3,
      system: 1,
      user: { email: 'carol@partner.cz', firstName: 'Carol', lastName: 'Brown' },
      role: 'write',
    },
  ],
  2: [
    {
      id: 4,
      system: 2,
      user: { email: 'david@techsol.cz', firstName: 'David', lastName: 'Horáček' },
      role: 'full',
    },
  ],
  3: [],
};

let nextSupplyPointId = 4;
let nextAccessId = 5;

export const SupplyPointService = {
  getList: async () => {
    return ApiClient.get('/system/', z.array(supplyPointSchema), undefined, {
      mock: () => [...mockSupplyPoints],
    });
  },

  getById: async (id: number) => {
    return ApiClient.get(`/system/${id}`, supplyPointSchema, undefined, {
      mock: () => {
        const item = mockSupplyPoints.find((sp) => sp.id === id);
        if (!item) throw new Error(`Supply point ${id} not found`);
        return { ...item };
      },
    });
  },

  create: async (data: CreateSupplyPointRequest) => {
    return ApiClient.post('/system/', data, supplyPointSchema, {
      mock: () => {
        const newItem: SupplyPoint = {
          ...createSupplyPointRequestSchema.parse(data),
          id: nextSupplyPointId++,
          partners: [],
          authorizedRole: 'full',
        };
        mockSupplyPoints.push(newItem);
        return { ...newItem };
      },
    });
  },

  update: async (id: number, data: CreateSupplyPointRequest) => {
    return ApiClient.put(`/system/${id}`, data, supplyPointSchema, {
      mock: () => {
        const index = mockSupplyPoints.findIndex((sp) => sp.id === id);
        if (index === -1) throw new Error(`Supply point ${id} not found`);
        mockSupplyPoints[index] = {
          ...mockSupplyPoints[index],
          ...createSupplyPointRequestSchema.parse(data),
        };
        return { ...mockSupplyPoints[index] };
      },
    });
  },

  partialUpdate: async (id: number, data: UpdateSupplyPointRequest) => {
    return ApiClient.patch(`/system/${id}`, data, supplyPointSchema, {
      mock: () => {
        const index = mockSupplyPoints.findIndex((sp) => sp.id === id);
        if (index === -1) throw new Error(`Supply point ${id} not found`);
        mockSupplyPoints[index] = {
          ...mockSupplyPoints[index],
          ...updateSupplyPointRequestSchema.parse(data),
        };
        return { ...mockSupplyPoints[index] };
      },
    });
  },

  delete: async (id: number) => {
    return ApiClient.delete(`/system/${id}`, undefined, z.null(), {
      mock: () => {
        const index = mockSupplyPoints.findIndex((sp) => sp.id === id);
        if (index !== -1) mockSupplyPoints.splice(index, 1);
        return null;
      },
    });
  },

  getTypes: async () => {
    return ApiClient.get('/system/types', z.array(supplyPointTypeSchema), undefined, {
      mock: () => [...mockTypes],
    });
  },

  getAccessList: async () => {
    return ApiClient.get('/system/access/', z.array(accessSchema), undefined, {
      mock: () =>
        Object.values(mockAccessesBySupplyPoint)
          .flat()
          .map((a) => ({ ...a })),
    });
  },

  getAccessById: async (id: number) => {
    return ApiClient.get(`/system/access/${id}`, accessSchema, undefined, {
      mock: () => {
        const item = Object.values(mockAccessesBySupplyPoint)
          .flat()
          .find((a) => a.id === id);
        if (!item) throw new Error(`Access ${id} not found`);
        return { ...item };
      },
    });
  },

  createAccess: async (supplyPointId: number, data: CreateAccessRequest) => {
    return ApiClient.post(
      `/system/${supplyPointId}/access`,
      // The system id is redundant with the URL, so it is set here rather than in the form.
      { ...data, system: supplyPointId },
      accessSchema,
      {
        mock: () => {
          const list =
            mockAccessesBySupplyPoint[supplyPointId] ??
            (mockAccessesBySupplyPoint[supplyPointId] = []);
          const emailParts = data.user.split('@')[0].split('.');
          const newItem: Access = {
            id: nextAccessId++,
            system: supplyPointId,
            user: {
              email: data.user,
              firstName: emailParts[0]
                ? emailParts[0].charAt(0).toUpperCase() + emailParts[0].slice(1)
                : '',
              lastName: emailParts[1]
                ? emailParts[1].charAt(0).toUpperCase() + emailParts[1].slice(1)
                : '',
            },
            role: data.role,
          };
          list.push(newItem);
          return { ...newItem };
        },
      }
    );
  },

  updateAccess: async (id: number, data: CreateAccessRequest) => {
    return ApiClient.put(`/system/access/${id}`, data, accessSchema, {
      mock: () => {
        for (const list of Object.values(mockAccessesBySupplyPoint)) {
          const index = list.findIndex((a) => a.id === id);
          if (index !== -1) {
            list[index] = { ...list[index], role: data.role };
            return { ...list[index] };
          }
        }
        throw new Error(`Access ${id} not found`);
      },
    });
  },

  partialUpdateAccess: async (id: number, data: UpdateAccessRequest) => {
    return ApiClient.patch(`/system/access/${id}`, data, accessSchema, {
      mock: () => {
        for (const list of Object.values(mockAccessesBySupplyPoint)) {
          const index = list.findIndex((a) => a.id === id);
          if (index !== -1) {
            if (data.role) list[index] = { ...list[index], role: data.role };
            return { ...list[index] };
          }
        }
        throw new Error(`Access ${id} not found`);
      },
    });
  },

  deleteAccess: async (id: number) => {
    return ApiClient.delete(`/system/access/${id}`, undefined, z.null(), {
      mock: () => {
        for (const list of Object.values(mockAccessesBySupplyPoint)) {
          const index = list.findIndex((a) => a.id === id);
          if (index !== -1) {
            list.splice(index, 1);
            break;
          }
        }
        return null;
      },
    });
  },

  addPartners: async (supplyPointId: number, partnerIds: number[]) => {
    return ApiClient.post(
      `/system/${supplyPointId}/partners/add`,
      { partners: partnerIds },
      z.object({}),
      {
        mock: () => {
          const sp = mockSupplyPoints.find((s) => s.id === supplyPointId);
          if (sp) {
            for (const id of partnerIds) {
              if (!sp.partners.some((p) => p.id === id)) {
                sp.partners.push({ id, name: `Organization ${id}` });
              }
            }
          }
          return {};
        },
      }
    );
  },

  removePartners: async (supplyPointId: number, partnerIds: number[]) => {
    return ApiClient.post(
      `/system/${supplyPointId}/partners/remove`,
      { partners: partnerIds },
      z.object({}),
      {
        mock: () => {
          const sp = mockSupplyPoints.find((s) => s.id === supplyPointId);
          if (sp) {
            sp.partners = sp.partners.filter((p) => !partnerIds.includes(p.id));
          }
          return {};
        },
      }
    );
  },
};
